import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosConfig";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Space,
  Typography,
  Modal,
  Alert,
  Spin,
  App,
} from "antd";
import { UserAddOutlined } from "@ant-design/icons";
import DashboardLayout from "./DashboardLayout";

const { Title } = Typography;
const { Option } = Select;

export default function AddUser() {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [apis, setApis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchApis();
  }, []);

  const fetchApis = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/api/apiCollection");
      setApis(response.data);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch API collection"
      );
      message.error("Failed to fetch API collections", 4);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    setError("");

    try {
      await axiosInstance.post("/api/user/users", {
        name: values.name,
        ApiCollectionNames: values.ApiCollectionNames || [],
      });
      message.success("User added successfully!", 3);
      setShowSuccess(true);
      form.resetFields();
      setTimeout(() => {
        navigate("/dashboard");
      }, 2000);
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Server error. Please try again later.";
      setError(errorMessage);
      message.error(errorMessage, 4);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout title="Add New User">
      <Card
        style={{
          width: "100%",
          maxWidth: "600px",
          margin: "0 auto",
          borderRadius: 12,
          boxShadow: "0 4px 16px rgba(0, 102, 255, 0.08)",
          border: "1px solid #e1e8f0",
        }}
      >
        <Space orientation="vertical" size="large" style={{ width: "100%" }}>
          <div style={{ textAlign: "center", marginBottom: "8px" }}>
            <Title
              level={2}
              style={{
                margin: 0,
                background: "linear-gradient(135deg, #0066ff 0%, #0052cc 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                fontWeight: 700,
              }}
            >
              <UserAddOutlined /> Enter User Details
            </Title>
          </div>

          {error && (
            <Alert
              title="Error"
              description={error}
              type="error"
              showIcon
              closable
              onClose={() => setError("")}
              style={{ borderRadius: 8 }}
            />
          )}

          <Spin spinning={loading}>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              autoComplete="off"
            >
              <Form.Item
                name="name"
                label="User Name"
                rules={[
                  { required: true, message: "Please input user name!" },
                ]}
              >
                <Input placeholder="Type user name..." size="large" />
              </Form.Item>

              <Form.Item
                name="ApiCollectionNames"
                label="Select API Collection/s"
                rules={[
                  {
                    required: true,
                    message: "Please select at least one API collection!",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select API Collections..."
                  size="large"
                  allowClear
                >
                  {apis.map((api) => (
                    <Option key={api.id} value={api.name}>
                      {api.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  size="large"
                  icon={<UserAddOutlined />}
                  block
                  style={{
                    background: "linear-gradient(135deg, #0066ff 0%, #0052cc 100%)",
                    border: "none",
                    fontWeight: 600,
                    height: "44px",
                    boxShadow: "0 4px 12px rgba(0, 102, 255, 0.3)",
                  }}
                >
                  Submit Details
                </Button>
              </Form.Item>
            </Form>
          </Spin>
        </Space>
      </Card>

      <Modal
        title={
          <span
            style={{
              fontSize: "20px",
              fontWeight: 700,
              background: "linear-gradient(135deg, #00d9ff 0%, #0066ff 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Success
          </span>
        }
        open={showSuccess}
        onOk={() => {
          setShowSuccess(false);
          navigate("/dashboard");
        }}
        onCancel={() => {
          setShowSuccess(false);
          navigate("/dashboard");
        }}
        okText="Go to Dashboard"
        okButtonProps={{
          size: "large",
          style: {
            background: "linear-gradient(135deg, #0066ff 0%, #0052cc 100%)",
            border: "none",
            fontWeight: 600,
          },
        }}
        cancelButtonProps={{ size: "large" }}
        style={{ borderRadius: 16 }}
      >
        <p style={{ fontSize: "14px", color: "#6b7a99" }}>
          New User Added Successfully!
        </p>
      </Modal>
    </DashboardLayout>
  );
}
